'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, 
  ChevronDown, 
  ChevronRight, 
  ShieldCheck, 
  Clock, 
  Settings, 
  Package, 
  ArrowRight,
  AlertTriangle,
  FileCheck,
  BookOpen,
  HelpCircle
} from 'lucide-react';
import UnifiedCTA from '@/components/sections/UnifiedCTA';
import JsonLd from '@/components/common/JsonLd';
import { businessData } from '@/config/businessData';

const IndustryTemplate = ({ data }) => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="w-full bg-white">
      <JsonLd 
        type="BreadcrumbList" 
        data={[
          { name: "Home", url: "/" },
          { name: "Industries", url: "/industries" },
          { name: data.title, url: `/industries/${data.slug}` }
        ]} 
      />
      <JsonLd 
        type="Service" 
        data={{
          name: data.title,
          description: data.hero.subtitle
        }} 
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[60vh] flex items-center justify-center pt-24 pb-16 overflow-hidden bg-[#0A1F33]">
        <div className="absolute inset-0 z-0">
          <Image
            src={data.hero.bgImage || '/images/advanced_label_infrastructure.png'}
            alt={`${data.title} Background`}
            fill
            className="object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F33] via-[#0A1F33]/80 to-transparent" />
        </div>
        
        <div className="relative z-10 w-full max-w-[1216px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-blue-600/20 text-blue-400 text-sm font-semibold tracking-wider uppercase mb-6 border border-blue-500/30">
              {data.hero.tagline}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-serif">
              {data.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              {data.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/quote">
                <button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] flex items-center justify-center gap-2">
                  Request a Quote
                  <ArrowRight size={20} />
                </button>
              </Link>
              <Link href="/contact">
                <button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/20 px-8 py-4 rounded-full font-bold text-lg transition-all flex items-center justify-center">
                  Talk to an Expert
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Points & Challenges Section */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="w-full max-w-[1216px] mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-serif">Built for Industry Demands</h2>
            <p className="text-lg text-slate-600">We understand the unique challenges of the {data.industryName} sector and engineer labels that perform under pressure.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {data.painPoints.map((point, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  {point.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{point.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{point.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Expanded Challenges Description */}
          {data.challengesExpanded && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-blue-50/50 border border-blue-100 rounded-3xl p-8 md:p-10 shadow-sm"
            >
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="p-3 bg-blue-600 text-white rounded-2xl shrink-0">
                  <AlertTriangle size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1F33] mb-4">Critical Industry Challenges Faced</h3>
                  <p className="text-slate-700 leading-relaxed text-base md:text-lg">
                    {data.challengesExpanded}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section className="py-20 bg-white">
        <div className="w-full max-w-[1216px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-serif">Advanced Manufacturing Capabilities</h2>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Our HSIIDC Rai facility is equipped with state-of-the-art 8-color flexographic presses and digital technology, allowing us to deliver unparalleled quality and customization for your products.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {data.capabilities.map((cap, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-slate-900">{cap.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{cap.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="w-full lg:w-1/2 relative">
              <div className="aspect-square md:aspect-[4/3] relative rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src={data.capabilitiesImage || '/images/infrastructure/machinery.jpg'}
                  alt="Manufacturing Capabilities"
                  fill
                  className="object-cover"
                />
              </div>
              
              <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 max-w-[240px] hidden md:block">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold text-xl">
                    100%
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Quality Assured</p>
                    <p className="text-xs text-slate-500">ISO & GMP Compliant</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Material Science & Adhesives */}
      {data.materialsDetails && (
        <section className="py-20 bg-slate-50 border-t border-b border-slate-200">
          <div className="w-full max-w-[1216px] mx-auto px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
              
              <div className="w-full lg:w-7/12">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-widest block mb-3">Substrates & Formulations</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-serif">Materials & Adhesive Engineering</h2>
                <div className="text-slate-700 leading-relaxed text-base md:text-lg space-y-6">
                  {data.materialsDetails.split('\n\n').map((paragraph, pIdx) => {
                    if (paragraph.trim().startsWith('-')) {
                      return (
                        <ul key={pIdx} className="list-disc pl-6 space-y-2.5 my-4">
                          {paragraph.split('\n').map((li, lIdx) => (
                            <li key={lIdx} className="text-slate-600 text-sm md:text-base">
                              {li.replace('-', '').trim()}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return <p key={pIdx}>{paragraph.trim()}</p>;
                  })}
                </div>
              </div>

              <div className="w-full lg:w-5/12 bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm">
                <h3 className="text-xl font-bold text-slate-950 mb-6 flex items-center gap-2">
                  <Settings size={22} className="text-blue-600" />
                  Application Specifications
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  {data.applicationDetails || "Our labels are custom-slit and finished to ensure flawless application on automatic bottling and labeling machines."}
                </p>
                <div className="border-t border-slate-100 pt-6 space-y-4">
                  <div className="flex justify-between items-center py-1 border-b border-slate-50">
                    <span className="text-xs font-bold text-slate-500 uppercase">Applicator Speed Compatibility</span>
                    <span className="text-sm font-bold text-slate-800">Up to 400 bpm</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-slate-50">
                    <span className="text-xs font-bold text-slate-500 uppercase">Standard Core Dimensions</span>
                    <span className="text-sm font-bold text-slate-800">3-inch (76mm) / Custom</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-xs font-bold text-slate-500 uppercase">Roll Unwind Orientation</span>
                    <span className="text-sm font-bold text-slate-800">All positions (1 - 8)</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Procurement & Specs */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="w-full max-w-[1216px] mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">Procurement Specifications</h2>
            <p className="text-lg text-slate-400">Streamlined ordering, reliable lead times, and massive production capacity tailored for B2B buyers.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Minimum Order Quantity (MOQ)', value: data.procurement.moq, icon: <Package size={24} /> },
              { label: 'Standard Lead Time', value: data.procurement.leadTime, icon: <Clock size={24} /> },
              { label: 'Production Capacity', value: data.procurement.capacity, icon: <Settings size={24} /> },
              { label: 'Sample Policy', value: data.procurement.sample, icon: <ShieldCheck size={24} /> },
            ].map((stat, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 p-6 rounded-2xl text-center flex flex-col items-center justify-center gap-4 hover:bg-white/10 transition-colors">
                <div className="text-blue-400">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Regulatory Compliance & Certification Standards */}
      {data.complianceDetails && (
        <section className="py-20 bg-white">
          <div className="w-full max-w-[1216px] mx-auto px-6">
            <div className="bg-emerald-50/40 border border-emerald-100 rounded-[32px] p-8 md:p-12 shadow-[0_10px_35px_rgba(16,185,129,0.02)] flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              
              <div className="w-full lg:w-5/12 text-center lg:text-left">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mb-6 mx-auto lg:mx-0">
                  <FileCheck size={32} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-950 mb-6 font-serif">Regulatory Compliance & Standards</h2>
                <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                  Every label batch leaves our Sonipat factory fully verified for material traceability and international safety certifications, protecting your brand from compliance audits.
                </p>
                <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
                  <span className="bg-emerald-100/50 text-emerald-800 text-xs font-bold px-3.5 py-1.5 rounded-full border border-emerald-200">ISO 9001:2015</span>
                  <span className="bg-emerald-100/50 text-emerald-800 text-xs font-bold px-3.5 py-1.5 rounded-full border border-emerald-200">GMP Standards</span>
                  <span className="bg-emerald-100/50 text-emerald-800 text-xs font-bold px-3.5 py-1.5 rounded-full border border-emerald-200">FDA Compliant Adhesives</span>
                </div>
              </div>

              <div className="w-full lg:w-7/12 border-t lg:border-t-0 lg:border-l border-slate-200/80 pt-8 lg:pt-0 lg:pl-12">
                <div className="text-slate-700 leading-relaxed text-sm md:text-base space-y-4">
                  {data.complianceDetails.split('\n').map((line, idx) => {
                    const cleanLine = line.trim();
                    if (cleanLine.startsWith('1.') || cleanLine.startsWith('2.') || cleanLine.startsWith('3.')) {
                      return (
                        <div key={idx} className="flex gap-3 items-start my-4">
                          <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-1" />
                          <p className="text-slate-700 font-medium">{cleanLine.substring(3)}</p>
                        </div>
                      );
                    }
                    return cleanLine && <p key={idx}>{cleanLine}</p>;
                  })}
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Product Gallery */}
      <section className="py-20 bg-slate-50">
        <div className="w-full max-w-[1216px] mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-serif">{data.industryName} Packaging Gallery</h2>
            <p className="text-lg text-slate-600">See our high-quality label solutions in action.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.gallery.map((img, idx) => (
              <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group shadow-sm border border-slate-200">
                <Image
                  src={img.src}
                  alt={img.alt}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <p className="text-white font-bold text-lg">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Real-World Case Study */}
      {data.caseStudy && (
        <section className="py-20 bg-[#0A1F33] text-white overflow-hidden relative">
          {/* Subtle decoration */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl"></div>

          <div className="w-full max-w-[1216px] mx-auto px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block py-1 px-3 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-4 border border-emerald-500/30">
                Success Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-serif">Case Study: {data.caseStudy.title}</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-sm">C</span>
                  <h3 className="text-lg font-bold text-slate-100">The Challenge</h3>
                </div>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                  {data.caseStudy.challenge}
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-sm">S</span>
                  <h3 className="text-lg font-bold text-slate-100">Our Solution</h3>
                </div>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                  {data.caseStudy.solution}
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm">R</span>
                  <h3 className="text-lg font-bold text-slate-100">The Result</h3>
                </div>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                  {data.caseStudy.result}
                </p>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="w-full max-w-[800px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-serif">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">Common questions about our {data.industryName} labels.</p>
          </div>
          
          <div className="space-y-4">
            {data.faqs.map((faq, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                >
                  <span className="font-bold text-left text-slate-900 flex items-center gap-2">
                    <HelpCircle size={16} className="text-slate-400 shrink-0" />
                    {faq.q}
                  </span>
                  <ChevronDown className={`transform transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 pt-2 text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50 text-sm sm:text-base">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Cross-Linking: Related Services */}
      <section className="py-12 bg-slate-50 border-t border-b border-slate-100">
        <div className="w-full max-w-[1216px] mx-auto px-6 text-center">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Explore Label Customization Options</h4>
          <div className="flex flex-wrap justify-center gap-4 text-xs font-bold text-slate-600">
            <Link href="/services/roll-form-labels" className="hover:text-blue-600 underline">Roll Form Labels</Link>
            <span className="text-slate-300">|</span>
            <Link href="/services/sheet-form-labels" className="hover:text-blue-600 underline">Sheet Form Labels</Link>
            <span className="text-slate-300">|</span>
            <Link href="/services/security-labels" className="hover:text-blue-600 underline">Security & Holographic Labels</Link>
            <span className="text-slate-300">|</span>
            <Link href="/services/digital-packaging-labels" className="hover:text-blue-600 underline">Digital Printing Options</Link>
          </div>
        </div>
      </section>

      <UnifiedCTA
        heading="Ready to upgrade your packaging?"
        description="Connect with our experts today to discuss your precision labeling requirements and request your custom sample pack."
        primaryLabel="Request a Quote"
        primaryHref="/quote"
        secondaryLabel="Contact Sales"
        secondaryHref="/contact"
        variant="blue"
      />
    </main>
  );
};

export default IndustryTemplate;
